import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Plus, ChevronDown, Download, Paperclip, FileText, Video, X } from "lucide-react";
import Select, { components } from "react-select";
import Layout from "../../components/Layout/Layout";
import BackLink from "../../components/Common/BackLink";
import LoadingState from "../../components/Common/LoadingState";
import { getProfile } from "../../api/userApi";
import { getMessageSessions, getChatMessages, getAttachmentDownloadUrl, sendChatMessage } from "../../api/postgre/chatApi";
import { getStoredUserId } from "../../utils/user";
import { useAsyncAction } from "../../hooks/useAsyncAction";
import { defaultReactSelectStyles } from "../../utils/reactSelectStyles";
import { showErrorToast } from "../../utils/toast";
import { getInvalidUploadFilenameMessage, isUploadFilenameValid } from "../../utils/fileUploadValidation";

const DropdownIndicator = (props) => (
    <components.DropdownIndicator {...props}>
        <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
    </components.DropdownIndicator>
);

const chatSelectStyles = {
    ...defaultReactSelectStyles,
    control: (base) => ({
        ...defaultReactSelectStyles.control(base),
        minHeight: "34px",
        height: "34px",
        fontSize: "12px",
    }),
    valueContainer: (base) => ({
        ...base,
        padding: "0 12px",
        flexWrap: "nowrap",
    }),
    singleValue: (base) => ({
        ...defaultReactSelectStyles.singleValue(base),
        fontSize: "12px",
        fontWeight: "500",
        maxWidth: "200px",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    }),
    indicatorsContainer: (base) => ({
        ...base,
        height: "34px",
    }),
    option: (base, state) => ({
        ...base,
        fontSize: "12px",
        backgroundColor: state.isSelected ? "#0f1b8f" : state.isFocused ? "#dfe7ff" : "#ffffff",
        color: state.isSelected ? "#ffffff" : "#334155",
    }),
};

const POLL_INTERVAL_MS = 15000;
const MAX_ATTACHMENTS = 3;

const normalizeMessage = (raw, currentUserId) => {
    const isCurrentUser = String(raw.UserId) === String(currentUserId);
    const attachments = Array.isArray(raw.Attachments)
        ? raw.Attachments
        : Array.isArray(raw.attachments)
            ? raw.attachments
            : [];
    return {
        id: raw.Id,
        sender: isCurrentUser ? "user" : "admin",
        name: raw.UserName || (isCurrentUser ? "" : "KORRES admin"),
        time: raw.CreatedOn || "",
        text: raw.Message || "",
        photoUrl: raw.ProfilePhotoUrl || "",
        attachments,
    };
};

const HelpContact = ({ activeLabel = "Help", forceNewChat = false }) => {
    const { t } = useTranslation();
    const { messageId: urlMessageId } = useParams();
    const [profilePhotoUrl, setProfilePhotoUrl] = useState("");
    const [isInitializing, setIsInitializing] = useState(true);
    const [sessions, setSessions] = useState([]);
    const [messageId, setMessageId] = useState(null);
    const [messages, setMessages] = useState([]);
    const [message, setMessage] = useState("");
    const [attachments, setAttachments] = useState([]);
    const [isComposerFocused, setIsComposerFocused] = useState(false);
    const [isLoadingMessages, setIsLoadingMessages] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const [hasSubmittedMessage, setHasSubmittedMessage] = useState(false);
    const [hasThreadScrolled, setHasThreadScrolled] = useState(false);
    const composerRef = useRef(null);
    const attachmentInputRef = useRef(null);
    const profileAction = useAsyncAction();
    const { run: runLoadProfile } = profileAction;
    const isComposerExpanded = isComposerFocused || message.trim().length > 0 || attachments.length > 0;
    const currentUserId = getStoredUserId();
    const isNewChat = messageId === 0;
    const unreadCount = sessions.reduce((total, item) => total + (Number(item.unreadCount) || 0), 0);

    const loadMessages = useCallback(async (msgId) => {
        try {
            const raw = await getChatMessages(msgId);
            const normalized = raw.map((item) => normalizeMessage(item, currentUserId));
            setMessages(normalized);
        } catch {
            // silently fail on poll
        }
    }, [currentUserId]);

    // Load profile + sessions on mount
    useEffect(() => {
        let mounted = true;

        const init = async () => {
            const profile = await runLoadProfile(
                () => getProfile(),
                { errorFallback: "Unable to load profile.", silentErrorToast: true }
            );
            if (!mounted) return;
            setProfilePhotoUrl(profile?.profilePhotoUrl || "");

            try {
                const sessionList = await getMessageSessions();
                if (!mounted) return;
                setSessions(sessionList);

                const requestedId = !forceNewChat && urlMessageId ? Number(urlMessageId) : null;
                const requestedSessionExists = requestedId != null
                    && sessionList.some((session) => session.messageId === requestedId);

                if (forceNewChat) {
                    // "New" was clicked explicitly — always start a fresh conversation
                    setMessageId(0);
                } else if (requestedSessionExists) {
                    // Open the specific conversation the user clicked in the Messages list
                    setMessageId(requestedId);
                    await loadMessages(requestedId);
                } else if (sessionList.length > 0) {
                    // Default to latest (first) session
                    const latestId = sessionList[0].messageId;
                    setMessageId(latestId);
                    await loadMessages(latestId);
                } else {
                    // No sessions — start in new chat mode
                    setMessageId(0);
                }
            } catch {
                setMessageId(0);
            }

            if (mounted) setIsInitializing(false);
        };

        init();
        return () => { mounted = false; };
    // urlMessageId/forceNewChat are only read for their value at mount — navigating
    // to a different message or to "New" always remounts this component via the
    // Messages list, so they're intentionally not reactive dependencies here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadMessages, runLoadProfile]);

    // Poll for new messages every 15s (only for real sessions)
    useEffect(() => {
        if (!messageId || messageId === 0) return;
        const interval = setInterval(() => loadMessages(messageId), POLL_INTERVAL_MS);
        return () => clearInterval(interval);
    }, [messageId, loadMessages]);

    // Clear "message posted" banner after 3s
    useEffect(() => {
        if (!hasSubmittedMessage) return;
        const timeout = setTimeout(() => setHasSubmittedMessage(false), 3000);
        return () => clearTimeout(timeout);
    }, [hasSubmittedMessage]);

    const handleSelectSession = async (selectedId) => {
        const id = Number(selectedId);
        setMessageId(id);
        setMessages([]);
        if (id !== 0) {
            setIsLoadingMessages(true);
            await loadMessages(id);
            setIsLoadingMessages(false);
        }
    };

    const handleNewChat = () => {
        setMessageId(0);
        setMessages([]);
        setMessage("");
        setAttachments([]);
        setIsComposerFocused(false);
    };

    const handleComposerBlur = () => {
        setTimeout(() => {
            const activeEl = document.activeElement;
            if (composerRef.current?.contains(activeEl)) return;
            if (!message.trim() && attachments.length === 0) setIsComposerFocused(false);
        }, 150);
    };

    const handleSend = async () => {
        const text = message.trim();
        if (!text && attachments.length === 0) return;
        if (messageId === null || messageId === undefined || isSending) return;

        const optimisticMsg = {
            id: `optimistic-${Date.now()}`,
            sender: "user",
            time: t("HELP_CHAT_JUST_NOW"),
            text,
            attachments,
        };
        setMessages((prev) => [...prev, optimisticMsg]);
        setMessage("");
        setAttachments([]);
        setIsComposerFocused(false);
        setIsSending(true);

        try {
            const result = await sendChatMessage(messageId, text, attachments);
            // If new session (messageId=0), API may return the real MessageId
            const newMsgId = result?.MessageId || result?.message_id || result?.messageId;
            if (newMsgId && newMsgId !== messageId) {
                setMessageId(newMsgId);
                const newSession = { messageId: newMsgId, label: `Conversation ${sessions.length + 1}` };
                setSessions((prev) => [newSession, ...prev]);
                setHasSubmittedMessage(true);
                await loadMessages(newMsgId);
            } else {
                setHasSubmittedMessage(true);
                if (messageId !== 0) await loadMessages(messageId);
            }
        } catch {
            // keep the optimistic message visible
        } finally {
            setIsSending(false);
        }
    };

    const handlePickAttachment = () => {
        attachmentInputRef.current?.click();
    };

    const prepareAttachment = (file) => {
        const isImage = file.type?.startsWith("image/");
        const isVideo = file.type?.startsWith("video/");
        return {
            id: `${file.name}-${Date.now()}`,
            kind: isImage ? "image" : isVideo ? "video" : "file",
            name: file.name,
            previewUrl: isImage || isVideo ? URL.createObjectURL(file) : "",
            file,
        };
    };

    const handleAttachmentChange = (event) => {
        const files = Array.from(event.target.files || []);
        event.target.value = "";
        if (!files.length) return;
        if (attachments.length + files.length > MAX_ATTACHMENTS) {
            showErrorToast(t("HELP_CHAT_ATTACHMENT_LIMIT", { count: MAX_ATTACHMENTS }));
            return;
        }

        for (const file of files) {
            if (!isUploadFilenameValid(file.name)) {
                showErrorToast(getInvalidUploadFilenameMessage());
                continue;
            }

            const attachment = prepareAttachment(file);
            setAttachments((prev) => [...prev, attachment]);
        }
    };

    const removeAttachment = (attachmentId) => {
        setAttachments((prev) => {
            const removed = prev.find((item) => item.id === attachmentId);
            if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
            return prev.filter((item) => item.id !== attachmentId);
        });
    };

    const handleAttachmentDownload = async (attachmentUrl, attachmentName) => {
        try {
            const downloadUrl = await getAttachmentDownloadUrl(attachmentUrl);
            const link = document.createElement("a");
            link.href = downloadUrl;
            link.download = attachmentName;
            link.rel = "noopener noreferrer";
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            showErrorToast(error?.message || t("HELP_CHAT_ATTACHMENT_DOWNLOAD_FAILED"));
        }
    };

    const handleThreadScroll = (event) => {
        const nextScrolled = event.currentTarget.scrollTop > 6;
        setHasThreadScrolled((prev) => (prev === nextScrolled ? prev : nextScrolled));
    };

    return (
        <Layout activeLabel={activeLabel}>
            {isInitializing ? (
                <LoadingState message={t("HELP_CHAT_LOADING")} />
            ) : (
                <div className="grid h-[calc(100dvh-4rem)] min-h-0 grid-rows-[auto_auto_minmax(0,1fr)_auto]">
                    <BackLink label={t("HELP_CHAT_BACK")} to={activeLabel === "Messages" ? "/messages" : "/help"} />

                    <div className="mt-8 flex flex-wrap items-center justify-between gap-1 border-b border-slate-200 pb-6">
                        <div>
                            <h1 className="text-lg font-semibold text-slate-900">
                                {t("HELP_CHAT_TITLE")}
                                {activeLabel === "Messages" && unreadCount > 0 ? (
                                    <span className="ml-2 text-base font-medium text-primary">
                                        {`${unreadCount} ${t("MESSAGES_NEW_SUFFIX")}`}
                                    </span>
                                ) : null}
                            </h1>
                            <p className="mt-1 text-sm text-slate-500">{t("HELP_CHAT_SUBTITLE")}</p>
                        </div>
                        <div className="flex items-center gap-1">
                            {activeLabel !== "Messages" && sessions.length > 0 && (
                                <Select
                                    value={(() => {
                                        if (isNewChat) return { value: 0, label: t("HELP_CHAT_NEW_CHAT") };
                                        const found = sessions.find((s) => s.messageId === messageId);
                                        return found ? { value: found.messageId, label: found.label } : null;
                                    })()}
                                    onChange={(option) => handleSelectSession(option.value)}
                                    options={[
                                        ...(isNewChat ? [{ value: 0, label: t("HELP_CHAT_NEW_CHAT") }] : []),
                                        ...sessions.map((s) => ({ value: s.messageId, label: s.label })),
                                    ]}
                                    styles={chatSelectStyles}
                                    components={{ DropdownIndicator }}
                                    isSearchable={false}
                                    className="w-48"
                                />
                            )}
                            {activeLabel !== "Messages" && sessions.length > 0 && (
                                <button
                                    type="button"
                                    onClick={handleNewChat}
                                    className="inline-flex items-center gap-1 rounded-md border border-primary px-3 py-2 text-xs font-semibold text-primary hover:bg-primary/5 focus:outline-none"
                                >
                                    <Plus className="h-3.5 w-3.5" />
                                    {t("HELP_CHAT_NEW_CHAT")}
                                </button>
                            )}
                            {hasSubmittedMessage && (
                                <div className="rounded-sm bg-primary px-3 py-2 text-xs text-white">
                                    {t("HELP_CHAT_MESSAGE_SENT")}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="relative mt-6 min-h-0">
                        <div className="h-full overflow-y-auto pr-2 scrollbar-thin" onScroll={handleThreadScroll}>
                            <div className="pb-6">
                                {/* Welcome message — always shown */}
                                <div className="border-y border-slate-200 py-6">
                                    <div className="flex items-start gap-3">
                                        <div className="h-10 w-10 shrink-0 overflow-hidden rounded-full bg-slate-200">
                                            <div className="flex h-full w-full items-center justify-center rounded-full bg-primary text-[6px] font-bold uppercase tracking-[0.08em] text-white">
                                                KORRES
                                            </div>
                                        </div>
                                        <div className="flex-1">
                                            <p className="text-sm font-semibold text-slate-700">KORRES admin</p>
                                            <div className="mt-4 space-y-1 text-sm leading-relaxed text-slate-600">
                                                <p>{t("HELP_CHAT_WELCOME_MESSAGE")}</p>
                                                <p>{t("HELP_CHAT_WELCOME_SUBTITLE")}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                {/* Conversation messages */}
                                {isLoadingMessages ? (
                                    <div className="flex items-center justify-center py-10">
                                        <div className="h-5 w-5 animate-spin rounded-full border-2 border-slate-200 border-t-primary" />
                                    </div>
                                ) : messages.map((item) => (
                                    <div
                                        key={item.id}
                                        className="border-b border-slate-200 py-6"
                                    >
                                        <div className="flex items-start gap-3">
                                            <div className={`flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full ${
                                                item.sender === "user" ? "bg-slate-200" : "bg-primary px-1 text-[8px] font-bold uppercase tracking-[0.08em] text-white"
                                            }`}>
                                                {item.sender === "user" ? (
                                                    profilePhotoUrl ? (
                                                        <img src={profilePhotoUrl} alt="Profile" className="h-full w-full object-cover" />
                                                    ) : (
                                                        <span className="text-[8px] font-bold uppercase tracking-[0.08em] text-white">KORRES</span>
                                                    )
                                                ) : (
                                                    <span className="text-[8px] font-bold uppercase tracking-[0.08em] text-white">KORRES</span>
                                                )}
                                            </div>
                                            <div className="flex-1">
                                                <p className="text-sm font-semibold text-slate-700">{item.name}</p>
                                                <p className="mt-0.5 text-xs text-slate-400">{item.time}</p>
                                                <div className="mt-4 space-y-1 text-sm leading-relaxed text-slate-600">
                                                    {item.text.split("\n").map((line, lineIndex) => (
                                                        <p key={`${item.id}-line-${lineIndex}`}>{line}</p>
                                                    ))}
                                                </div>
                                                {Array.isArray(item.attachments) && item.attachments.length > 0 ? (
                                                    <div className="mt-6 flex flex-wrap gap-3">
                                                        {item.attachments.map((attachment, attachmentIndex) => {
                                                            const savedUrl = attachment.SavedUrl || attachment.AttachmentUrl || attachment.attachmentUrl || attachment.mediaUrl || attachment.url || attachment.fileUrl || "";
                                                            const attachmentUrl = attachment.DownloadUrl || attachment.downloadUrl || "";
                                                            const attachmentName = attachment.name || attachment.FileName || attachment.fileName || attachment.fileNameOriginal || t("HELP_CHAT_ATTACHMENT_FILE");
                                                            const extension = attachmentName.split(".").pop()?.toLowerCase();
                                                            const inferredKind = ["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg"].includes(extension)
                                                                ? "image"
                                                                : ["mp4", "webm", "mov", "m4v", "avi"].includes(extension) ? "video" : "file";
                                                            const attachmentKind = String(attachment.kind || attachment.type || inferredKind).toLowerCase();
                                                            return (
                                                                <div
                                                                    key={`${item.id}-attachment-${attachmentIndex}`}
                                                                    className="group relative h-28 w-28 shrink-0 overflow-hidden rounded-lg bg-slate-100"
                                                                >
                                                                    {attachmentKind === "image" && attachmentUrl ? (
                                                                        <img
                                                                            src={attachmentUrl}
                                                                            alt={attachmentName}
                                                                            className="h-full w-full object-cover"
                                                                        />
                                                                    ) : attachmentKind === "video" && attachmentUrl ? (
                                                                        <video
                                                                            src={attachmentUrl}
                                                                            controls
                                                                            className="h-full w-full object-cover"
                                                                        />
                                                                    ) : (
                                                                        <div className="flex h-full flex-col items-center justify-center gap-2 px-2 text-center">
                                                                            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-slate-500">
                                                                                <FileText className="h-4 w-4" />
                                                                            </div>
                                                                            <p className="w-full truncate text-xs font-medium text-slate-600">{attachmentName}</p>
                                                                        </div>
                                                                    )}
                                                                    {savedUrl ? (
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => handleAttachmentDownload(savedUrl, attachmentName)}
                                                                            className="absolute right-1.5 top-1.5 flex h-7 w-7 items-center justify-center rounded-full bg-white/90 text-slate-600 opacity-0 shadow transition-opacity hover:bg-white hover:text-primary focus:opacity-100 focus:outline-none group-hover:opacity-100"
                                                                            aria-label={`Download ${attachmentName}`}
                                                                            title={`Download ${attachmentName}`}
                                                                        >
                                                                            <Download className="h-4 w-4" />
                                                                        </button>
                                                                    ) : null}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                ) : null}
                                            </div>
                                        </div>
                                    </div>
                                ))
                                }
                            </div>
                        </div>
                        <div
                            className={`pointer-events-none absolute inset-x-0 top-0 h-10 bg-gradient-to-b from-white/90 via-white/55 to-transparent transition-opacity duration-150 ${
                                hasThreadScrolled ? "opacity-100" : "opacity-0"
                            }`}
                        />
                    </div>

                    <div className="mt-3 border-t border-slate-200 bg-transparent py-3">
                        <div ref={composerRef} className="flex items-start gap-4 px-2">
                            <div className="h-10 w-10 overflow-hidden rounded-full bg-slate-200">
                                {profilePhotoUrl ? (
                                    <img src={profilePhotoUrl} alt="Profile" className="h-full w-full object-cover" />
                                ) : (
                                    <div className="flex h-full w-full items-center justify-center rounded-full bg-primary px-1 text-[8px] font-bold uppercase tracking-[0.08em] text-white">
                                        KORRES
                                    </div>
                                )}
                            </div>
                            <div className="flex-1">
                                {isComposerExpanded ? (
                                    <>
                                        <div className="rounded-sm bg-[#f3f3f3] px-3 py-2">
                                            <textarea
                                                autoFocus
                                                value={message}
                                                onChange={(event) => setMessage(event.target.value)}
                                                onFocus={() => setIsComposerFocused(true)}
                                                onBlur={handleComposerBlur}
                                                placeholder={t("HELP_CHAT_PLACEHOLDER_EXPANDED")}
                                                className="h-20 w-full resize-none bg-transparent text-sm text-slate-600 placeholder:text-slate-400 focus:outline-none"
                                            />
                                            {attachments.length > 0 ? (
                                            <div className="mt-2 flex flex-wrap gap-2 border-t border-slate-200 pt-2">
                                                {attachments.map((attachment) => (
                                                    attachment.kind === "image" || attachment.kind === "video" ? (
                                                        <div key={attachment.id} className="relative h-[72px] w-[72px] shrink-0 overflow-hidden rounded-lg">
                                                            {attachment.kind === "video" ? (
                                                                <video
                                                                    src={attachment.previewUrl}
                                                                    muted
                                                                    className="h-full w-full object-cover"
                                                                />
                                                            ) : (
                                                                <img
                                                                    src={attachment.previewUrl}
                                                                    alt={attachment.name}
                                                                    className="h-full w-full object-cover"
                                                                />
                                                            )}
                                                            {attachment.kind === "video" && (
                                                                <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                                                                    <Video className="h-4 w-4 text-white" />
                                                                </div>
                                                            )}
                                                            <button
                                                                type="button"
                                                                onClick={() => removeAttachment(attachment.id)}
                                                                className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-black/60 text-white hover:bg-black/80 focus:outline-none"
                                                                aria-label={t("COMMON_REMOVE")}
                                                            >
                                                                <X className="h-3 w-3" />
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <div key={attachment.id} className="relative flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-700 shadow-sm">
                                                            <FileText className="h-4 w-4 shrink-0 text-slate-500" />
                                                            <span className="max-w-[120px] truncate">{attachment.name}</span>
                                                            <button
                                                                type="button"
                                                                onClick={() => removeAttachment(attachment.id)}
                                                                className="flex h-5 w-5 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-600 focus:outline-none"
                                                                aria-label={t("COMMON_REMOVE")}
                                                            >
                                                                <X className="h-3 w-3" />
                                                            </button>
                                                        </div>
                                                    )
                                                ))}
                                            </div>
                                        ) : null}
                                        </div>
                                        <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
                                            <div className="flex items-center gap-2">
                                                <button
                                                    type="button"
                                                    onClick={handleSend}
                                                    className="h-8 min-w-[104px] rounded bg-primary px-4 text-xs font-medium text-white disabled:cursor-not-allowed disabled:opacity-60"
                                                    disabled={(!message.trim() && attachments.length === 0) || isSending}
                                                >
                                                    {isSending ? t("HELP_CHAT_SENDING") : t("HELP_CHAT_SEND")}
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => { setMessage(""); setAttachments([]); setIsComposerFocused(false); }}
                                                    className="h-8 rounded border border-slate-200 bg-white px-4 text-xs font-medium text-slate-600 hover:bg-slate-50 focus:outline-none"
                                                >
                                                    {t("COMMON_CANCEL")}
                                                </button>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={handlePickAttachment}
                                                className="inline-flex items-center gap-2 rounded-sm text-xs font-medium text-slate-500 hover:text-slate-700 focus:outline-none"
                                            >
                                                <Paperclip className="h-3.5 w-3.5" />
                                                {t("HELP_CHAT_ATTACH")}
                                            </button>
                                        </div>
                                    </>
                                ) : (
                                    <input
                                        type="text"
                                        value={message}
                                        onChange={(event) => setMessage(event.target.value)}
                                        onFocus={() => setIsComposerFocused(true)}
                                        onBlur={handleComposerBlur}
                                        placeholder={t("HELP_CHAT_PLACEHOLDER")}
                                        className="h-10 w-full rounded-sm bg-[#f3f3f3] px-4 text-sm text-slate-600 placeholder:text-slate-400 focus:outline-none"
                                    />
                                )}
                                <input
                                    ref={attachmentInputRef}
                                    type="file"
                                    accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip"
                                    multiple
                                    className="hidden"
                                    onChange={handleAttachmentChange}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </Layout>
    );
};

export default HelpContact;
